import React from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

interface State {
  error: Error | null;
}

/**
 * Last line of defence against a blank page.
 *
 * A render-time throw anywhere in the tree used to unmount the whole app and
 * leave the user on an empty white screen with no hint that anything had gone
 * wrong, let alone a way out. This catches it and offers the two things that
 * actually recover: reload, or go back to the dashboard.
 */
class ErrorBoundary extends React.Component<{ children: React.ReactNode }, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Keep the detail in the console for bug reports; never show a stack to the
    // user.
    console.error('Unhandled UI error:', error, info.componentStack);
  }

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div className="min-h-screen app-surface flex items-center justify-center p-6">
        <div className="card max-w-md w-full text-center">
          <div className="inline-flex p-3.5 rounded-2xl bg-red-500/10 border border-red-500/20 mb-5">
            <AlertTriangle className="h-7 w-7 text-red-400" />
          </div>
          <h1 className="text-xl font-black text-ink mb-2">Something broke on our side</h1>
          <p className="text-sm text-ink-dim leading-relaxed mb-6">
            This page hit an unexpected error. Your data is safe — reloading usually clears it.
          </p>
          <div className="flex flex-col sm:flex-row gap-2.5">
            <button onClick={() => window.location.reload()} className="btn-primary flex-1">
              <RefreshCw className="h-4 w-4 mr-2" />
              Reload page
            </button>
            {/* A full navigation, not a router push: the router itself may be
                part of what failed. */}
            <button
              onClick={() => {
                window.location.href = '/dashboard';
              }}
              className="btn-outline flex-1"
            >
              <Home className="h-4 w-4 mr-2" />
              Back to dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
